import type { Subscription } from "rxjs";
import { zip as observableZip } from "rxjs";

import type { IComponentController, IScope } from "angular";
import "angular-translate";
import type { Moment } from "moment-timezone";
import { utc } from "moment-timezone";
import { finalize } from "rxjs/operators";

import type {
    IEvent,
    IEventsGroup,
    IFunctionality,
    IIssue,
    IResponse,
    ISiteEquipmentNormalized,
    ISiteSlim,
} from "App/Modules/Models";
import {
    EventAssessmentClass,
    EventTypeFunctions,
    IssueTypeFunctions,
    EventClassification,
    Progress,
} from "App/Modules/Models";
import type {
    IEventsRepository,
    IIssuesRepository,
    ISiteEquipmentRepository,
    ISiteFunctionalitiesRepository,
    ISitesRepository,
} from "App/Modules/Repositories";
import type { IEventsTableEntry } from "./EventsTable/Models/IEventsTableEntry";
import type { EventsTableEntryType, EventsTableEntryTypeFilter } from "./EventsTable/Models/EventsTableEntryType";

export default class LatestEventsComponentController implements IComponentController {
    public static $inject: string[] = [
        "$scope",
        "$translate",
        "mmEventsRepository",
        "mmIssuesRepository",
        "mmSiteEquipmentRepository",
        "mmSiteFunctionalitiesRepository",
        "mmSitesRepository",
    ];

    public siteEquipment: ISiteEquipmentNormalized[];

    public functionalities: IFunctionality[];

    public unfilteredEvents: IEvent[];
    public events: IEvent[];

    public unFilteredIssues: IIssue[];
    public issues: IIssue[];

    public unfilteredEventsTableEntries: IEventsTableEntry[];
    public eventsTableEntries: IEventsTableEntry[];

    public siteId: string;

    public site: ISiteSlim;

    public dateFrom: Moment;

    public dateTo: Moment;
    public dateToPlusOne: Moment; // 00:00 the day after the selected date, to make the selected date inclusive

    public isSiteDataLoading = true;

    public currentSiteSubscription: Subscription;

    public errorSiteResponse: IResponse<ISiteSlim>;

    public isEventsDataLoading = true;

    public currentEventsAndIssuesSubscription: Subscription;

    public errorEventsResponse: IResponse<IEventsGroup> | IResponse<IIssue[]>;

    public isSiteObjectsDataLoading = true;

    public currentSiteObjectsSubscription: Subscription;

    public errorSiteObjectsResponse: IResponse<ISiteEquipmentNormalized[]> | IResponse<IFunctionality[]>;

    public activeFilter: EventsTableEntryTypeFilter = "All";

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly eventsRepository: IEventsRepository,
        private readonly issuesRepository: IIssuesRepository,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
        private readonly siteFunctionalitiesRepository: ISiteFunctionalitiesRepository,
        private readonly sitesRepository: ISitesRepository,
    ) {}

    public $onInit() {
        this.fetchSite();
    }

    public onSiteLoaded() {
        // dateTo is set to local 00:00 the selecetd date, to keep it in sync with the date picker.
        // When using the date, 1 day is added to it, such that the date becomes inclusive.
        this.dateTo = utc().tz(this.site.timeZone).startOf("date");
        this.dateToPlusOne = this.dateTo.clone().add(1, "days");
        this.dateFrom = this.dateTo.clone().subtract(3, "days");

        this.fetchEvents();
        this.fetchSiteObjects();

        const updateDateTo = (newValue: Moment, prevValue: Moment) => {
            if (prevValue.isSame(newValue)) {
                return;
            }

            // the dateFrom / dateTo will be set to 00:00 local time, so add 1 day to dateTo to
            // include the entire dateTo day.
            this.dateToPlusOne = this.dateTo.clone().add(1, "days");
            this.fetchEvents();
        };

        const updateDateFrom = (newValue: Moment, prevValue: Moment) => {
            if (prevValue.isSame(newValue)) {
                return;
            }
            this.fetchEvents();
        };

        this.$scope.$watch("$ctrl.dateTo", updateDateTo);
        this.$scope.$watch("$ctrl.dateFrom", updateDateFrom);
    }

    public getEvent = (eventId: string) => {
        return this.events.find((e) => e.id === eventId);
    };

    public filterButtonClicked(button: EventsTableEntryType) {
        if (button === this.activeFilter) {
            this.activeFilter = "All";
        } else {
            this.activeFilter = button;
        }
        this.updateFilteredData();
    }

    public isFilterActive(filter: EventsTableEntryType) {
        return this.activeFilter === filter;
    }

    private updateFilteredData() {
        switch (this.activeFilter) {
            case "All":
                this.events = this.unfilteredEvents;
                this.eventsTableEntries = this.unfilteredEventsTableEntries;
                this.issues = this.unFilteredIssues;
                break;
            case "Event":
                this.events = this.unfilteredEvents;
                this.eventsTableEntries = this.unfilteredEventsTableEntries.filter(
                    (te) => te.entryTypeBase === "Event",
                );
                this.issues = [];
                break;
            case "Issue":
                this.events = [];
                this.eventsTableEntries = this.unfilteredEventsTableEntries.filter(
                    (te) => te.entryTypeBase === "Issue",
                );
                this.issues = this.unFilteredIssues;
                break;
            default:
                throw Error("Unknown EventsTableEntryTypeFilter");
        }
    }

    private fetchSite() {
        if (this.currentSiteSubscription) {
            this.currentSiteSubscription.unsubscribe();
        }
        this.isSiteDataLoading = true;

        this.currentSiteSubscription = this.sitesRepository
            .getSiteSlim(this.siteId)
            .pipe(
                finalize(() => {
                    this.isSiteDataLoading = false;
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                (response) => {
                    if (!response.hasError && response.data) {
                        this.site = response.data;
                        this.onSiteLoaded();
                    }
                },
                (error) => {
                    this.errorSiteResponse = error;
                },
            );
    }

    private fetchSiteObjects() {
        if (this.currentSiteObjectsSubscription) {
            this.currentSiteObjectsSubscription.unsubscribe();
        }

        const siteEquipment$ = this.siteEquipmentRepository.getForSite(this.siteId);
        const functionalities$ = this.siteFunctionalitiesRepository.getFunctionalitiesForSite(this.siteId);

        this.isSiteObjectsDataLoading = true;

        this.currentSiteObjectsSubscription = observableZip(siteEquipment$, functionalities$)
            .pipe(
                finalize(() => {
                    this.isSiteObjectsDataLoading = false;
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                ([siteEquipmentResponse, functionalitiesResponse]) => {
                    this.siteEquipment = siteEquipmentResponse.data;
                    this.functionalities = functionalitiesResponse.data;
                },
                (err: IResponse<ISiteEquipmentNormalized[]> | IResponse<IFunctionality[]>) => {
                    this.errorSiteObjectsResponse = err;
                },
            );
    }

    private fetchEvents() {
        const dtFrom = this.dateFrom.format();
        const dtToPlusOne = this.dateToPlusOne.format();

        if (this.currentEventsAndIssuesSubscription) {
            this.currentEventsAndIssuesSubscription.unsubscribe();
        }

        const eventsObservable$ = this.eventsRepository.getEvents(this.siteId, dtFrom, dtToPlusOne, null, null, null);

        const issuesObservable$ = this.issuesRepository.getIssuesForSite(this.siteId, dtFrom, dtToPlusOne);

        this.isEventsDataLoading = true;

        this.currentEventsAndIssuesSubscription = observableZip(eventsObservable$, issuesObservable$)
            .pipe(
                finalize(() => {
                    this.isEventsDataLoading = false;
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                ([eventsResponse, issuesResponse]) => {
                    this.unfilteredEvents = eventsResponse.data.events;
                    this.unFilteredIssues = issuesResponse.data;

                    // Note: The sourceName is what is currently shown in the list. For issues, this can be
                    // set to a composite name such as "NVR 1 / Video stream 2". For events, we just set it to the
                    // SiteEquipmentName for now, but could add e.g. alarm zone info or whatever in the future.

                    this.unfilteredEventsTableEntries = [
                        ...this.unfilteredEvents
                            .filter((event) => this.isEventInSelectedInterval(this.dateFrom, this.dateToPlusOne, event))
                            .map((e) => {
                                const entry: IEventsTableEntry = {
                                    dtEntry: e.dtEvent,
                                    entryStatusText: this.getAlarmStatusText(e),
                                    entryTypeBase: "Event",
                                    entryTypeName: e.typeName,
                                    entryTypeNameLocalized: e.typeNameLocalized,
                                    friendlyId: e.friendlyId,
                                    icon: EventTypeFunctions.eventTypeBaseIcon(e.eventType),
                                    id: e.friendlyId,
                                    image: e.image,
                                    showDetails: false,
                                    siteEquipmentId: e.siteEquipmentId,
                                    siteEquipmentName: e.siteEquipmentName,
                                    // Interrim hack since siteEquipment is not always set currently
                                    sourceName: e.siteEquipmentName
                                        ? `${e.siteEquipmentName} / ${e.functionalityName}`
                                        : e.functionalityName,
                                };

                                return entry;
                            }),
                        ...this.unFilteredIssues
                            .filter((issue) => this.isIssueInSelectedInterval(this.dateFrom, this.dateToPlusOne, issue))
                            .map((i) => {
                                const entry: IEventsTableEntry = {
                                    dtEntry: i.dtIssue,
                                    entryStatusText: this.getIssueStatusText(i),
                                    entryTypeBase: "Issue",
                                    entryTypeName: i.issueTypeName,
                                    entrySubTypeName: i.issueSubTypeName,
                                    entryTypeNameLocalized: `${i.issueTypeNameLocalized} - ${i.issueSubTypeNameLocalized}`,
                                    friendlyId: "",
                                    icon: IssueTypeFunctions.issueTypeIcon(i.issueType),
                                    id: i.id,
                                    image: null,
                                    isAcknowledged: i.isAcknowledged,
                                    isActive: i.isActive,
                                    issueSeverity: i.severity,
                                    issueSeverityName: i.severityName as string,
                                    showDetails: false,
                                    siteEquipmentId: i.affectedMainSiteEquipment?.id,
                                    siteEquipmentName: i.affectedMainSiteEquipment?.name,
                                    sourceName: i.source.name,
                                };
                                return entry;
                            }),
                    ];

                    this.updateFilteredData();
                },
                (err: IResponse<IEventsGroup> | IResponse<IIssue[]>) => {
                    this.errorEventsResponse = err;
                },
            );
    }

    private getIssueStatusText: (issue: IIssue) => string = (issue: IIssue) => {
        let statusText: string;

        if (issue.isActive) {
            statusText = "Active";
        } else {
            statusText = "NotActive";
        }

        if (issue.isAcknowledged) {
            statusText += "Acknowledged";
        } else {
            statusText += "NotAcknowledged";
        }

        return statusText;
    };

    // Return the translation key to use in the Status column of the alarm list
    // (one of the keys in Gui.Manage.SiteEventsTable.StatusText)
    private getAlarmStatusText(event: IEvent) {
        if (event.isClassifiedAsValidByIris === true) {
            // Visible for partners
            if (!event.eventAssessmentClass) {
                // Includes the case eventAssessmentClass == Unknown, since Unknown=0
                return "Pending";
            }
            return EventAssessmentClass[event.eventAssessmentClass];
        }

        // isClassifiedByIris === false below. Only visible for sysadmins
        if (
            !event.eventClassification ||
            event.eventClassification === EventClassification.Unknown ||
            event.eventClassification === EventClassification.L2HumanActivityUncertain
        ) {
            if (event.progress === Progress.Failed || event.progress === Progress.FailedMissingId) {
                return "Failed";
            }
            // Due to inconsistent handling in the back-end, this is actually not necessarily "L3 pending".
            // For sites connected directly to primary ARC, isClassifiedAsValidByIris = false until the primary
            // ARC starts handling the alarm, so we would return L3Pending also for those sites, even if there
            // is no L3 connected. For now, this is handled by setting a translation for this enum value that is
            // just "Pending (SysAdmin only)". In the future, we should reconsider how the
            // isClassifiedAsValidByIris flag is handled when there is no pre-ARC.
            return "L3Pending";
        }

        if (event.eventClassification === EventClassification.L2HumanActivityNo) {
            return "L2Rejected";
        }

        // Any other classification enum implies rejected by L3 (could be due to any of the non-human enum values)
        return "L3Rejected";
    }

    private isEventInSelectedInterval(dateFrom: Moment, dateTo: Moment, event: IEvent) {
        if (event.dtEvent.isBetween(dateFrom, dateTo)) {
            return true;
        }

        return false;
    }

    private isIssueInSelectedInterval(dateFrom: Moment, dateTo: Moment, issue: IIssue) {
        const issueStart = issue.dtIssue.clone();

        if ((!issue.issueDurationSeconds || issue.isActive) && issueStart.isSameOrBefore(dateFrom)) {
            return true;
        }

        const issueEnd = issueStart.clone().add(issue.issueDurationSeconds, "s");

        if (issueStart.isBetween(dateFrom, dateTo) || issueEnd.isBetween(dateFrom, dateTo)) {
            return true;
        }

        return false;
    }
}
